import React, { useState } from 'react';
import { Row, Col, Button, Input, Select, Form, message } from 'antd';
import { deviceFactory } from '@iso/services';
import _ from 'lodash';
import IntlMessages from '@iso/components/utility/intlMessages';
import AddDeviceModalWrapper from './AddDeviceModal.styles';
import { deviceTypes } from '@iso/constants/deviceTypes';

const { Option } = Select;

const AddDeviceModal = (props) => {
  const { propertyId, onSaved } = props;

  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Track the selected device type so we can show the RealPTT-only device-id
  // field. RealPTT is onboarded by its portal device id (Device.original_id),
  // not just a display name, so the generic name field isn't enough for it.
  const [deviceType, setDeviceType] = useState(null);

  const renderOptionsDeviceType = () => {
    let options = [];
    _.forEach(deviceTypes, (type, index) => {
      if (type.autoRegistered) {
        return;
      }
      options.push(
        <Option key={index} value={type.value}>
          <div>{type.label}</div>
        </Option>
      );
    });
    return (
      <Form.Item name="device_type" className="mb-2" rules={[{ required: true }]}>
        <Select
          placeholder="Select a device type"
          allowClear
          size="large"
          style={{ width: '100%' }}
          onChange={(value) => setDeviceType(value)}
        >
          {options}
        </Select>
      </Form.Item>
    );
  };

  const cancelModal = () => {
    props.cancelAddDeviceModal();
  };

  const handleSubmit = () => {
    // Defensive guard — the parent renders the +Add button regardless
    // of whether a property is selected, and activePropertyId starts as
    // null in Redux until the user picks one. Submitting with no
    // property_id sends a payload the backend can't route as an admin
    // registration; for NEYOS it falls through to the webhook ingest
    // path and returns 403 from the HMAC check, which the caller has
    // no way to act on. Catch it here with a useful message instead.
    if (!propertyId) {
      message.error('Please select a property before adding a device.');
      return;
    }

    form
      .validateFields()
      .then(async (values) => {
        // Always source property_id from the current prop rather than
        // from form values. The form is mounted with forceRender=true
        // (so the Modal renders before it's visible) and AntD's
        // initialValues only applies at first mount — if the user picks
        // a property AFTER this component first rendered (the common
        // case, since activePropertyId boots as null), the form's
        // hidden property_id stays at its mount-time value (null)
        // forever. Pulling propertyId from props at submit time
        // bypasses that staleness without needing to babysit form
        // state via useEffect / setFieldsValue.
        const payload = { ...values, property_id: propertyId };

        setSubmitting(true);
        try {
          const res = await deviceFactory
            .resolveService(values.device_type)
            .storeOrUpdate(payload);

          if (res && res.code === '0000') {
            form.resetFields();
            cancelModal();
            onSaved(res);
            return;
          }

          // 200 from the backend with a typed-error code: telemetry not
          // found, device already registered, forbidden property, etc.
          // Surface the server's message verbatim — it's the most
          // actionable thing the operator can get.
          message.error(
            (res && res.message) ||
              'The server rejected the request without explaining why. Please try again or contact support.'
          );
        } catch (err) {
          // Network failure, 4xx without a JSON body, 5xx — anything
          // that makes axios reject. Without this catch the inner
          // promise rejection escapes the outer async then() (which
          // doesn't return it) and becomes an unhandled rejection,
          // producing the silent-button-click symptom reported on prod.
          console.error('Error during storeOrUpdate:', err);

          // Pull the server message out of the rejected axios response
          // when one is available; it usually carries the real reason
          // (e.g. "Authentication required", "Invalid webhook signature")
          // which is more useful than a generic banner.
          const serverMessage =
            err && err.response && err.response.data && err.response.data.message;
          message.error(
            serverMessage || 'Failed to save the device. Please try again.'
          );
        } finally {
          setSubmitting(false);
        }
      })
      .catch((info) => {
        // form.validateFields() rejection — a required field is missing
        // or empty. AntD already paints the offending field; we just
        // log for support without piling a toast on top of the field
        // hint.
        console.log('Validate Failed:', info);
      });
  };

  return (
    <AddDeviceModalWrapper
      forceRender={true}
      visible={props.visible}
      title={<IntlMessages id="safeFarmPage.device.modal.title" />}
      onCancel={cancelModal}
      maskClosable={false}
      footer={[
        <Button key="back" onClick={cancelModal} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
        <Button
          key="submit"
          onClick={handleSubmit}
          loading={submitting}
          className="btn-success"
          type="primary"
        >
          {<IntlMessages id="propertyPage.modal.save" />}
        </Button>
      ]}
    >
      <Form
        form={form}
        name="addDevice"
        scrollToFirstError
        fields={fields}
        style={{ display: 'block' }}
      >
        <Row gutter={[10, 15]}>
          <Col xs={{ span: 24 }} md={{ span: 24 }}>
            {renderOptionsDeviceType()}

            <Form.Item
              name="name"
              className="isoInputWrapper"
              rules={[
                {
                  required: true,
                  message: 'Please input your device name',
                },
                {
                  whitespace: true,
                  message: 'Please input your name',
                },
              ]}
            >
              <Input placeholder="Device Name" />
            </Form.Item>

            {deviceType === 'realptt' && (
              <Form.Item
                name="device_id"
                className="isoInputWrapper"
                preserve={false}
                rules={[
                  {
                    required: true,
                    message: 'Please input the AirAgri PTT device id',
                  },
                  {
                    whitespace: true,
                    message: 'Please input the AirAgri PTT device id',
                  },
                ]}
              >
                <Input placeholder="AirAgri PTT Device ID" />
              </Form.Item>
            )}
          </Col>
        </Row>
      </Form>
    </AddDeviceModalWrapper>
  );
};

export default AddDeviceModal;
